import icons from '@groovepages/gd-ui-kit/assets/icons-canvas-toolbar.svg?fileloader';
import shortid from 'shortid';
import Element from './elements/Element';

const utils = {
  gpAttributesString:
    '[data-gp-style], [data-gp-text], [data-gp-component], [data-gp-dnd]',
  gpAttributesArray: [
    'data-gp-style',
    'data-gp-text',
    'data-gp-component',
    'data-gp-dnd',
    'data-gp-attributes',
  ],
  breakpointLabels(breakpoints) {
    return [
      'all',
      ...Object.values(breakpoints).map((label) => `(min-width: ${label})`),
    ];
  },
  getMenuButton(icon) {
    const button = document.createElement('button');
    button.classList.add(`gp-menu-${icon}`);
    button.innerHTML = `
    <svg class="gp-menu-button">
      <use href="${icons}#${icon}" xlink:href="/images/${icons}.svg?fileloader#${icon}"></use>
    </svg>`;
    button.classList.add('gp-component-menu-button', 'focus:outline-none');
    return button;
  },
  getComponentLabel(element) {
    if (element.hasAttribute('data-gp-component-label')) {
      return element.getAttribute('data-gp-component-label');
    }
    if (element.hasAttribute('data-gp-block')) return 'Block';
    if (element.hasAttribute('data-gp-container')) {
      return 'Container';
    }
    if (
      element.hasAttribute('data-gp-component') &&
      element.getAttribute('data-gp-component') !== 'null'
    ) {
      return element.getAttribute('data-gp-component');
    }
    if (element.hasAttribute('data-gp-text')) return 'Text';
    if (element.hasAttribute('data-gp-style')) {
      return 'StyleableElement';
    }
    if (element.hasAttribute('data-gp-dnd')) {
      return 'DraggableElement';
    }
  },
  triggerEvent(el, eventName, options) {
    const event = new CustomEvent(eventName, { detail: options });
    el.dispatchEvent(event);
  },
  reinitializeScripts(content) {
    const load = [];
    const scripts = content.querySelectorAll('script:not(.gp-script-reinit)');
    for (let i = 0; i < scripts.length; i++) {
      if (!scripts[i].id) scripts[i].id = 'script-' + shortid.generate();
      if (content.querySelector(`.gp-script-reinit-${scripts[i].id}`)) {
        continue;
      }
      const script = document.createElement('SCRIPT');
      script.classList.add(
        `gp-script-reinit-${scripts[i].id}`,
        'gp-script-reinit'
      );
      if (scripts[i].hasAttribute('src')) {
        script.setAttribute('src', scripts[i].getAttribute('src'));
        load.push(
          new Promise((resolve) => {
            script.addEventListener('load', () => {
              resolve();
            });
          })
        );
      } else script.innerHTML = scripts[i].innerHTML;
      try {
        content.body.appendChild(script);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('embed error');
        // eslint-disable-next-line no-console
        console.log(scripts[i].innerHTML);
      }
    }
    return new Promise((resolve) => {
      Promise.all(load).then(() => {
        resolve();
      });
    });
  },
  wrapElements(elementToCheck) {
    const selectorToCheck = '[data-gp-text]';
    const classToAdd = 'gp-text-container';
    elementToCheck.querySelectorAll(selectorToCheck).forEach((element) => {
      if (!element.classList.contains(classToAdd)) {
        const wrapper = document.createElement('div');
        wrapper.classList.add(classToAdd);
        element.parentNode.replaceChild(wrapper, element);
        element.removeAttribute('data-gp-component-id');
        element.removeAttribute('data-gp-component');
        element.classList.remove('gp-component');
        wrapper.appendChild(element);
        utils.gpAttributesArray.forEach((attribute) => {
          if (element.hasAttribute(attribute)) {
            const value = element.getAttribute(attribute);
            element.removeAttribute(attribute);
            wrapper.setAttribute(attribute, value);
          }
        });
      }
    });
  },
  getComponentIdFromClass(element) {
    let id = null;
    element.classList.forEach((className) => {
      if (className.indexOf('gp-component-id-') !== -1)
        id = className.replace('gp-component-id-', '');
    });
    return id;
  },
  reflashComponentAttributes(element) {
    [...element.classList].forEach((className) => {
      if (className.indexOf('data-gp-attribute-') === -1) return;
      const attribute = className.replace('data-gp-attribute-', '');
      const value = [...element.classList].find(
        (valueClass) =>
          valueClass.indexOf(`data-gp-attributevalue-${attribute}`) !== -1
      );
      if (value)
        element.setAttribute(
          attribute,
          value.replace(`data-gp-attributevalue-${attribute}-`, '')
        );
    });
  },
  clearComponentIdClass(element) {
    const toClear = [];
    element.classList.forEach((className) => {
      if (className.indexOf('gp-component-id-') !== -1) toClear.push(className);
    });
    toClear.forEach((className) => {
      element.classList.remove(className);
      element.querySelectorAll(`.${className}`).forEach((child) => {
        child.classList.remove(className);
      });
    });
  },
  clearOrphanedStyles(canvas) {
    canvas
      .querySelectorAll('style[data-gp-styled-element]')
      .forEach((stylesheet) => {
        const element = stylesheet.getAttribute('data-gp-styled-element');
        if (!canvas.querySelector(`.gp-component-id-${element}`)) {
          stylesheet.remove();
        }
      });
  },
  componentCloned(cloneElement, eventBus) {
    this.setupClonedComponent(cloneElement, eventBus);
    cloneElement
      .querySelectorAll(
        this.gpAttributesString + ', [class*="gp-component-id-"]'
      )
      .forEach((element) => {
        this.setupClonedComponent(element, eventBus);
      });
  },
  setupClonedComponent(element, eventBus) {
    if (
      element.classList.contains('ql-editor') ||
      element.classList.contains('ql-clipboard') ||
      element.classList.contains('ql-tooltip')
    )
      return;
    const newId = this.uniqueId();
    const oldId = this.getComponentIdFromClass(element);
    this.clearComponentIdClass(element);
    element.classList.add(`gp-component-id-${newId}`);
    if (element.getAttribute('data-gp-component-id')) {
      element.setAttribute('data-gp-component-id', newId);
      eventBus.$emit('component-cloned', {
        from: oldId,
        to: newId,
      });
    } else {
      eventBus.$emit('text-node-cloned', {
        from: oldId,
        to: newId,
      });
    }
  },
  setupComponents(canvas) {
    canvas.querySelectorAll(this.gpAttributesString).forEach((element) => {
      if (element.getAttribute('data-gp-component-id')) {
        return;
      }
      const id = this.getComponentIdFromClass(element) || this.uniqueId();
      element.setAttribute('data-gp-component-id', id);
      element.classList.add('gp-component');
      element.classList.add(`gp-component-id-${id}`);
    });
  },
  checkForDuplicates(content) {
    const canvas = content.ownerDocument || content;
    const duplicates = [];
    content
      .querySelectorAll('[class*="gp-component-id-"]')
      .forEach((component) => {
        const id = this.getComponentIdFromClass(component);
        const elements = canvas.querySelectorAll(`.gp-component-id-${id}`);
        if (elements.length > 1) {
          duplicates.push(elements[elements.length - 1]);
        }
      });
    return [...new Set(duplicates)];
  },
  getNode(id, canvas) {
    return canvas.querySelector(`[data-gp-component-id="${id}"]`);
  },
  getComponent(id, eventBus, canvas) {
    const node = canvas.querySelector(`[data-gp-component-id="${id}"]`);
    if (!node) return null;
    return new Element(node, eventBus);
  },
  mergeOverlapping(value, toMerge) {
    const merge = (mergeFrom, mergeTo) => {
      for (const key in mergeFrom) {
        if (
          mergeFrom[key] !== null &&
          typeof mergeFrom[key] === 'object' &&
          mergeTo[key]
        ) {
          merge(mergeFrom[key], mergeTo[key]);
        } else if (mergeFrom[key] !== mergeTo[key]) {
          mergeTo[key] = null;
        }
      }
    };
    merge(toMerge, value);
  },
  removeOverlapping(value, toMerge) {
    const merge = (mergeFrom, mergeTo) => {
      for (const key in mergeFrom) {
        if (
          mergeFrom[key] !== null &&
          typeof mergeFrom[key] === 'object' &&
          mergeTo[key]
        ) {
          merge(mergeFrom[key], mergeTo[key]);
        } else if (mergeFrom[key] === mergeTo[key]) {
          mergeTo[key] = null;
        }
      }
    };
    merge(toMerge, value);
  },
  mergeOverwritten(value, toMerge) {
    const merge = (mergeFrom, mergeTo) => {
      for (const key in mergeFrom) {
        if (
          mergeFrom[key] !== null &&
          typeof mergeFrom[key] === 'object' &&
          mergeTo[key]
        ) {
          merge(mergeFrom[key], mergeTo[key]);
        } else if (mergeFrom[key] !== null) {
          mergeTo[key] = mergeFrom[key];
        }
      }
    };
    merge(toMerge, value);
  },
  uniqueId() {
    return this.quillSafeID(shortid.generate());
  },
  quillSafeID(id) {
    return id.split('-').join('0');
  },
};
export default utils;
